import type { BarcodeScanner as BarcodeScannerPort } from "../../application/ports/food-source";
import { AdaptiveBarcodeScanner } from "./adaptive-barcode-scanner";

/** Port adapter used when the application owns the operation, not the preview UI. */
export class ApplicationBarcodeScanner implements BarcodeScannerPort {
  constructor(
    private readonly scanner = new AdaptiveBarcodeScanner(),
  ) {}

  async scan(signal?: AbortSignal): Promise<string> {
    const abort = new AbortController();
    const dialog = createScannerDialog(() => abort.abort(new DOMException("Scan cancelled", "AbortError")));
    const visibilityChanged = () => {
      if (document.visibilityState === "hidden") abort.abort(new DOMException("Page hidden", "AbortError"));
    };
    const pageHidden = () => abort.abort(new DOMException("Page hidden", "AbortError"));
    document.addEventListener("visibilitychange", visibilityChanged);
    window.addEventListener("pagehide", pageHidden, { once: true });
    document.body.append(dialog.element);
    dialog.show();
    try {
      return await this.scanner.scan(dialog.video, { signal: combineSignals(signal, abort.signal) });
    } finally {
      document.removeEventListener("visibilitychange", visibilityChanged);
      window.removeEventListener("pagehide", pageHidden);
      dialog.close();
      dialog.element.remove();
    }
  }
}

function createScannerDialog(cancel: () => void): {
  element: HTMLDialogElement;
  video: HTMLVideoElement;
  show(): void;
  close(): void;
} {
  const element = document.createElement("dialog");
  element.setAttribute("aria-labelledby", "barcode-scanner-title");
  element.style.cssText = [
    "position:fixed", "inset:0", "width:100vw", "height:100dvh", "max-width:none", "max-height:none",
    "margin:0", "padding:0", "border:0", "background:#07120d", "color:white", "z-index:2147483647",
  ].join(";");
  const panel = document.createElement("div");
  panel.style.cssText = "position:relative;width:100%;height:100%;display:grid;grid-template-rows:auto 1fr;overflow:hidden";
  const header = document.createElement("header");
  header.style.cssText = "display:flex;align-items:center;justify-content:space-between;gap:1rem;padding:max(1rem,env(safe-area-inset-top)) 1rem 1rem;background:rgba(0,0,0,.72);z-index:2";
  const copy = document.createElement("div");
  const title = document.createElement("strong");
  title.id = "barcode-scanner-title";
  title.textContent = "Scan a food barcode";
  title.style.cssText = "display:block;font:600 1.05rem system-ui,sans-serif";
  const instruction = document.createElement("span");
  instruction.textContent = "Hold the barcode inside the camera view.";
  instruction.style.cssText = "display:block;margin-top:.25rem;font:400 .85rem system-ui,sans-serif;color:#d7e4dd";
  copy.append(title, instruction);
  const cancelButton = document.createElement("button");
  cancelButton.type = "button";
  cancelButton.textContent = "Cancel";
  cancelButton.style.cssText = "min-width:4.75rem;min-height:2.75rem;padding:.65rem 1rem;border:1px solid #b8c9c0;border-radius:999px;background:#fff;color:#15231b;font:600 1rem system-ui,sans-serif";
  cancelButton.addEventListener("click", cancel);
  header.append(copy, cancelButton);

  const video = document.createElement("video");
  video.autoplay = true;
  video.muted = true;
  video.playsInline = true;
  video.setAttribute("aria-label", "Live camera preview for barcode scanning");
  video.style.cssText = "width:100%;height:100%;object-fit:cover;background:#000";
  panel.append(header, video);
  element.append(panel);
  element.addEventListener("cancel", (event) => { event.preventDefault(); cancel(); });
  return {
    element,
    video,
    show: () => {
      if (typeof element.showModal === "function") element.showModal();
      else element.setAttribute("open", "");
      cancelButton.focus();
    },
    close: () => {
      if (element.open && typeof element.close === "function") element.close();
      else element.removeAttribute("open");
    },
  };
}

function combineSignals(...signals: Array<AbortSignal | undefined>): AbortSignal {
  const available = signals.filter((candidate): candidate is AbortSignal => Boolean(candidate));
  if (typeof AbortSignal.any === "function") return AbortSignal.any(available);
  const controller = new AbortController();
  for (const candidate of available) {
    if (candidate.aborted) controller.abort(candidate.reason);
    else candidate.addEventListener("abort", () => controller.abort(candidate.reason), { once: true });
  }
  return controller.signal;
}
